import Layout from 'antd/es/layout';
import Splitter from 'antd/es/splitter';
import theme from 'antd/es/theme';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import SideBar from './SideBar';

import { usePersistentState } from '@utils/hooks';
import { screenSizes } from '@utils/theme';

const Content = styled(Layout.Content)<{
  $backgroundColor: string;
  $borderRadius: number;
}>`
  display: flex;
  border-radius: ${(props) => props.$borderRadius}px 0
    ${(props) => props.$borderRadius}px ${(props) => props.$borderRadius}px;
  background-color: ${(props) => props.$backgroundColor};
`;

const TeamLayout = ({ children }: { children: ReactNode }) => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const isDraggingRef = useRef(false);
  const firstRenderRef = useRef(true);
  const [collapsed, setCollapsed] = usePersistentState(
    'teamSidebarCollapsed',
    window.innerWidth < screenSizes.tablet,
  );

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (!firstRenderRef.current) {
        setWindowWidth(window.innerWidth);
        setCollapsed(window.innerWidth < screenSizes.tablet);
      }
      firstRenderRef.current = false;
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [width, setWidth] = useState(300);
  useEffect(() => {
    if (!isDraggingRef.current) {
      if (collapsed) {
        setWidth(80);
      } else {
        setWidth(300);
      }
    }
  }, [collapsed]);

  return (
    <>
      {windowWidth <= screenSizes.mobile ? (
        <Layout>
          <Content
            $backgroundColor={colorBgContainer}
            $borderRadius={borderRadiusLG}
          >
            {children}
          </Content>
        </Layout>
      ) : (
        <Splitter
          onResizeStart={() => {
            isDraggingRef.current = true;
          }}
          onResizeEnd={() => {
            isDraggingRef.current = false;
          }}
          onResize={([firstPanelWidth]) => {
            if (isDraggingRef.current) {
              setWidth(firstPanelWidth);
            }
            if (collapsed && firstPanelWidth > 80) {
              setCollapsed(false);
            } else if (!collapsed && firstPanelWidth <= 80) {
              setCollapsed(true);
            }
          }}
        >
          <Splitter.Panel
            size={width}
            defaultSize={collapsed ? 80 : 300}
            max={collapsed ? 81 : '67%'}
            min={collapsed ? 80 : undefined}
            style={{ overflowX: 'hidden' }}
          >
            <Layout.Sider
              style={{ height: '100%' }}
              width={collapsed ? undefined : '100%'}
              collapsed={collapsed}
            >
              <SideBar
                setCollapsed={(input) => {
                  if (typeof input === 'boolean') {
                    setCollapsed(input);
                  } else {
                    setCollapsed(input(collapsed));
                  }
                }}
                collapsed={collapsed}
              />
            </Layout.Sider>
          </Splitter.Panel>
          <Splitter.Panel>
            <Layout style={{ height: '100%' }}>
              <Content
                $backgroundColor={colorBgContainer}
                $borderRadius={borderRadiusLG}
              >
                {children}
              </Content>
            </Layout>
          </Splitter.Panel>
        </Splitter>
      )}
    </>
  );
};

export default TeamLayout;
